export type ResourceType =
  | "video"
  | "article"
  | "guide"
  | "form"
  | "template"
  | "folder"
  | "policy"
  | "call";

export type ResourceCategory =
  | "Getting started"
  | "Product"
  | "Pricing & ROI"
  | "Intake & Planning"
  | "Communications & Marketing"
  | "Sample Documents"
  | "Compliance"
  | "Reference";

export interface Resource {
  id: string;
  title: string;
  description: string;
  type: ResourceType;
  category: ResourceCategory;
  url: string;
  /** e.g. "3 min watch", "5 min read" */
  duration?: string;
  /** Pinned to the top in the "Featured" sort and marked with a gold star. */
  featured?: boolean;
}

export const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  video: "Video",
  article: "Article",
  guide: "Guide",
  form: "Form",
  template: "Template",
  folder: "Folder",
  policy: "Policy",
  call: "Talk to us",
};

const TOOLKIT_BASE =
  "https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/toolkit";

// Featured order — the default sort in the drawer.
export const RESOURCES: Resource[] = [
  {
    id: "learning-center",
    title: "Learning Center",
    description:
      "Self-paced courses and estate planning scenarios, with a certificate at the end.",
    type: "guide",
    category: "Getting started",
    url: "https://content.buildxcl.com/?m=40b363dc-b030-11f0-9c7a-5abc8ea51e38&n=ddc26ccb-f6b7-11f0-b847-7ec8010dfd8b",
    featured: true,
  },
  {
    id: "pricing-page",
    title: "Plans and pricing",
    description: "Full pricing details for every tier, side by side.",
    type: "article",
    category: "Pricing & ROI",
    url: "https://www.getsnug.com/pricing",
    duration: "2 min read",
    featured: true,
  },
  {
    id: "estate-planning-faqs",
    title: "Estate Planning FAQs",
    description:
      "Legal-approved answers to common client questions, helping you steer clear of the unauthorized practice of law.",
    type: "guide",
    category: "Communications & Marketing",
    url: `${TOOLKIT_BASE}/Estate+Planning+FAQs.pdf`,
  },
  {
    id: "preparing-for-estate-planning",
    title: "Preparing for Estate Planning",
    description:
      "Worksheet and guide covering every decision a client makes when creating their estate plan.",
    type: "guide",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Estate+Planning+Preparation+-+Worksheet+%26+Guide.pdf`,
  },
  {
    id: "individual-vs-joint-trust",
    title: "Individual vs. Joint Trust Guide",
    description:
      "Helps couples understand and decide between Individual and Joint Trusts as the foundation of their estate plan.",
    type: "guide",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Individual+vs+Joint+Trust+Guide.pdf`,
  },
  {
    id: "trust-funding-guide",
    title: "Trust Funding General Guide",
    description:
      "The process and considerations for moving bank accounts, investments, real estate, and other assets into a Trust.",
    type: "guide",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Trust+Funding+General+Guide.pdf`,
  },
  {
    id: "intake-forms",
    title: "Intake Forms",
    description:
      "A folder of pen-and-paper intake forms — Revocable Living Trust, Will, Health Care Directive, Financial Power of Attorney, and the consent for intake form use.",
    type: "folder",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Intake+Forms.zip`,
  },
  {
    id: "client-email-templates",
    title: "Client Email Templates",
    description:
      "Sample email marketing templates — a good starting point for the value proposition and pitch to clients.",
    type: "template",
    category: "Communications & Marketing",
    url: `${TOOLKIT_BASE}/Client+Email+Templates.zip`,
  },
  {
    id: "postcard-high-touch",
    title: "Postcard (High Touch)",
    description:
      "Postcard template for high-touch engagement campaigns that drive estate planning conversations.",
    type: "template",
    category: "Communications & Marketing",
    url: `${TOOLKIT_BASE}/Whitelabeled+Postcard+-+High+Touch.pdf`,
  },
  {
    id: "postcard-low-touch",
    title: "Postcard (Low Touch)",
    description:
      "Postcard template for low-touch automated outreach that scales estate planning awareness.",
    type: "template",
    category: "Communications & Marketing",
    url: `${TOOLKIT_BASE}/Whitelabeled+Postcard+-+Low+Touch.pdf`,
  },
  {
    id: "sample-documents",
    title: "Sample Documents",
    description:
      "A folder of completed sample estate planning documents — Will, Pour Over Will, Joint Trust, Certification of Trust, Health Care Directive, and Financial Power of Attorney.",
    type: "folder",
    category: "Sample Documents",
    url: `${TOOLKIT_BASE}/Sample+Documents.zip`,
    featured: true,
  },
  {
    id: "compliance-bundle",
    title: "Compliance Bundle",
    description:
      "A folder of compliance materials — Compliance Overview, Sample ADV Clause, Terms & Conditions, Security Policy, and Business Continuity Plan.",
    type: "folder",
    category: "Compliance",
    url: `${TOOLKIT_BASE}/Compliance+Bundle.zip`,
    featured: true,
  },
  {
    id: "upl-guide",
    title: "Guide to Unauthorized Practice of Law",
    description:
      "How to avoid the unauthorized practice of law (UPL) and how the platform is designed to support that.",
    type: "guide",
    category: "Reference",
    url: `${TOOLKIT_BASE}/Guide+to+Avoiding+Unauthorized+Practice+of+Law+(UPL).pdf`,
  },
];
